<script setup>
// Page 5 — where the money actually goes: the two flows + the trust footnote. This is
// the ONE page that makes the "Pandig raakt het geld niet aan" claim, and it makes it
// structurally: two separate rails, split by an "apart gehouden" divider, the dues one
// never passing through Pandig. Every other page had to give the claim up so this one
// could be believed.
//
// The heading resolves, then the flow figure's two rails animate themselves (FlowRail
// owns its own scroll-triggered draw + pulse), and the regulatory footnote follows
// last — but from DIM, never from nothing: the mandate and supervision notes are the
// smallest print and the biggest objection on the page, so they can't be the thing a
// visitor scrolls past unrendered.
//
// Scroll-scrubbed over a taller sticky section (the same shape as LandingStakes,
// FeatureDeck, LandingHow, and LandingPricing), not a once-triggered reveal on a
// single-viewport .snap-page: FlowRail's own draw + pulse already scrub
// to scroll, but a single viewport of scroll room meant you could fling straight
// past the whole section before either rail finished travelling. Pinning it for
// longer guarantees the scroll distance those rails (and this heading/footnote)
// need actually exists.
import { ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { Landmark } from "@lucide/vue";
import FlowRail from "@/components/landing/FlowRail.vue";
import BrandMark from "@/components/BrandMark.vue";
import { gsap, ScrollTrigger, useMotion, reducedMotion, EASE, SCRUB, DIM } from "@/lib/motion";

const { t } = useI18n();

const root = ref(null);
const geldstroomHeading = ref(null);
const trustRow = ref(null);

let cleanup = () => {};

onMounted(() => {
  cleanup = useMotion(root.value, () => {
    if (reducedMotion) return;

    gsap.set([geldstroomHeading.value, trustRow.value], { opacity: DIM, y: 24 });

    const tl = gsap
      .timeline({ paused: true })
      .to(geldstroomHeading.value, { opacity: 1, y: 0, duration: 0.55, ease: EASE })
      .to(trustRow.value, { opacity: 1, y: 0, duration: 0.55, ease: EASE }, "+=0.3");

    ScrollTrigger.create({
      trigger: root.value,
      start: "top top",
      end: "bottom bottom",
      scrub: SCRUB,
      animation: tl,
    });
  });
});

onUnmounted(() => cleanup());
</script>

<template>
  <!-- Its content fades in across the section's own scroll range, so jumping to
       the section's top edge would land on a blank panel: a menu/pager jump goes
       to the settled end instead (see scrollElementIntoView in lib/scroll.js). -->
  <section
    id="geldstroom"
    ref="root"
    class="relative border-b-2 border-foreground bg-card"
    :class="reducedMotion ? 'page-panel flex items-center' : 'h-[135vh] sm:h-[162vh]'"
    data-jump="settled"
    :data-page-label="t('landing.pager.flows')"
  >
    <div :class="reducedMotion ? 'w-full' : 'page-camera flex [align-items:safe_center] overflow-y-auto lg:items-center lg:overflow-hidden'">
      <div class="mx-auto w-full max-w-6xl px-5 py-3 sm:py-10">
        <div ref="geldstroomHeading" class="max-w-2xl">
          <h2 class="font-sans text-xl font-bold leading-tight tracking-tight text-foreground sm:text-3xl">{{ t("landing.promise.title") }}</h2>
          <!-- The lead summarises what the figure below states outright: two rails,
               split by an "apart gehouden" divider, each with its own note. On a
               phone that summary is the one thing here that can go without losing
               anything — the diagram is the argument, and the screen it costs is
               what stops the diagram itself being cut in half. -->
          <p class="mt-2.5 hidden text-sm leading-relaxed text-muted-foreground sm:mt-4 sm:block sm:text-base">{{ t("landing.promise.lead") }}</p>
        </div>

        <figure class="mt-3.5 border-2 border-foreground bg-background p-2.5 text-foreground shadow-[var(--shadow-hard-lg)] sm:mt-8 sm:p-6">
          <FlowRail
            :eyebrow="t('landing.promise.dues_title')"
            :from-icon="BrandMark"
            :from-label="t('landing.promise.dues_from')"
            :amount-cents="475000"
            :to-icon="Landmark"
            :to-label="t('landing.promise.dues_to')"
            :landed-suffix="t('landing.flow.landed_suffix')"
            :pending-suffix="t('landing.flow.pending_suffix')"
            variant="primary"
          />
          <p class="mt-2 max-w-md text-xs leading-snug text-muted-foreground sm:mt-2 sm:ml-[calc(8rem+1.25rem)] sm:text-sm sm:leading-relaxed">{{ t("landing.promise.dues_body") }}</p>

          <div class="relative my-3 sm:my-6" aria-hidden="true">
            <div class="border-t-2 border-dashed border-foreground/20" />
            <span class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-3 font-mono text-[0.625rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              {{ t("landing.promise.divider") }}
            </span>
          </div>

          <FlowRail
            :eyebrow="t('landing.promise.fee_title')"
            :from-icon="Landmark"
            :from-label="t('landing.promise.fee_from')"
            :amount-cents="3800"
            :to-icon="BrandMark"
            :to-label="t('landing.promise.fee_to')"
            :landed-suffix="t('landing.flow.landed_suffix')"
            :pending-suffix="t('landing.flow.pending_suffix')"
            variant="muted"
            :start-delay="0.35"
          />
          <!-- The fee note sits out the phone layout (the rail itself already reads
               "de VvE → Pandig", and the price page states this in full): it's three
               lines on a 390px screen, and this page has none to spare. -->
          <p class="mt-2 hidden max-w-md text-xs leading-snug text-muted-foreground sm:mt-2 sm:ml-[calc(8rem+1.25rem)] sm:block sm:text-sm sm:leading-relaxed">{{ t("landing.promise.fee_body") }}</p>
        </figure>

        <!-- Two columns on a phone as well as on a desktop: this is small print, and set
             one under the other it costs more height than the figure it footnotes. -->
        <div ref="trustRow" data-testid="trust" class="mt-2.5 grid grid-cols-2 gap-2.5 border-t-2 border-foreground/10 pt-2.5 sm:mt-8 sm:gap-8 sm:pt-6">
          <div>
            <p class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.1em] text-primary sm:text-[0.6875rem]">{{ t("landing.trust.mollie_label") }}</p>
            <p class="mt-1 text-xs leading-snug text-muted-foreground sm:mt-1.5 sm:text-sm sm:leading-relaxed">{{ t("landing.trust.mollie") }}</p>
          </div>
          <div>
            <p class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.1em] text-primary sm:text-[0.6875rem]">{{ t("landing.trust.mandate_label") }}</p>
            <p class="mt-1 text-xs leading-snug text-muted-foreground sm:mt-1.5 sm:text-sm sm:leading-relaxed">{{ t("landing.trust.mandate") }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
